import type { DarkpoolLevelCluster, DarkpoolLevelsResponse, DarkpoolRailReceipt } from "./api";

export const DARKPOOL_ENDPOINT = "/api/darkpool/SPY/price-levels";
export const STOCK_VOLUME_ENDPOINT = "/api/stock/SPY/stock-volume-price-levels";

export interface DarkpoolRailRow {
  price: string;
  off: number;
  other: number;
  total: number;
}

export interface DarkpoolRailColumn {
  endpoint: string;
  title: string;
  ok: true;
  servedTradingDate: string;
  ageS: number | null;
  stale: boolean | null;
  rows: DarkpoolRailRow[];
  max: number;
}

export interface DarkpoolRailColumnRefusal {
  endpoint: string;
  title: string;
  ok: false;
  refused: "RAIL_ABSENT" | "NO_LEVELS" | "PAYLOAD_INVALID";
  detail: string;
}

export type DarkpoolRailModel =
  | { ok: true; root: "SPY"; columns: [DarkpoolRailColumn | DarkpoolRailColumnRefusal, DarkpoolRailColumn | DarkpoolRailColumnRefusal] }
  | { ok: false; refused: "ROOT_MISMATCH" | "PAYLOAD_INVALID"; detail: string };

type ColumnRule = {
  endpoint: string;
  title: string;
  off: "dark_pool_volume" | "off_vol";
  other: "regular_volume" | "lit_vol";
};

const RULES: readonly ColumnRule[] = [
  { endpoint: DARKPOOL_ENDPOINT, title: "DARK POOL", off: "dark_pool_volume", other: "regular_volume" },
  { endpoint: STOCK_VOLUME_ENDPOINT, title: "OFF-EXCH · LIT", off: "off_vol", other: "lit_vol" },
];
const DECIMAL = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function refusal(rule: ColumnRule, refused: DarkpoolRailColumnRefusal["refused"], detail: string): DarkpoolRailColumnRefusal {
  return { endpoint: rule.endpoint, title: rule.title, ok: false, refused, detail };
}

function volume(value: unknown): number | null {
  if (typeof value !== "string" || !DECIMAL.test(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function column(
  rule: ColumnRule,
  receipt: DarkpoolRailReceipt | undefined,
  clusters: readonly DarkpoolLevelCluster[],
): DarkpoolRailColumn | DarkpoolRailColumnRefusal {
  if (!record(receipt) || receipt.present !== true) {
    return refusal(rule, "RAIL_ABSENT", `${rule.endpoint} is absent or reports present=false`);
  }
  if (!record(receipt.dates)
    || typeof receipt.rows_total !== "number" || !Number.isInteger(receipt.rows_total) || receipt.rows_total < 0
    || typeof receipt.limit_capped !== "boolean"
    || typeof receipt.served_trading_date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(receipt.served_trading_date)
    || typeof receipt.newest_receipt_ts !== "string" || !Number.isFinite(Date.parse(receipt.newest_receipt_ts))
    || typeof receipt.age_s !== "number" || !Number.isFinite(receipt.age_s) || receipt.age_s < 0
    || typeof receipt.stale !== "boolean") {
    return refusal(rule, "PAYLOAD_INVALID", `${rule.endpoint} carries invalid or absent receipt metadata`);
  }
  const selected = clusters.filter((row) => row.source_endpoint === rule.endpoint);
  if (selected.length === 0) return refusal(rule, "NO_LEVELS", `${rule.endpoint} is present but serves no clusters`);

  const rows: DarkpoolRailRow[] = [];
  for (const item of selected) {
    if (typeof item.price_level !== "string" || !DECIMAL.test(item.price_level)) {
      return refusal(rule, "PAYLOAD_INVALID", `${rule.endpoint} carries an invalid price_level`);
    }
    const off = volume(item[rule.off]);
    const other = volume(item[rule.other]);
    if (off === null || other === null) {
      return refusal(rule, "PAYLOAD_INVALID", `${rule.endpoint} carries a null or invalid filled volume`);
    }
    rows.push({ price: item.price_level, off, other, total: off + other });
  }
  rows.sort((left, right) => Number(right.price) - Number(left.price));
  const drawn = rows.slice(0, 10);
  return {
    endpoint: rule.endpoint,
    title: rule.title,
    ok: true,
    servedTradingDate: typeof receipt.served_trading_date === "string" ? receipt.served_trading_date : "UNKNOWN",
    ageS: typeof receipt.age_s === "number" && Number.isFinite(receipt.age_s) ? receipt.age_s : null,
    stale: typeof receipt.stale === "boolean" ? receipt.stale : null,
    rows: drawn,
    max: drawn.reduce((largest, row) => Math.max(largest, row.total), 0),
  };
}

/** The served age plus the time the reply has been held, so STALE arrives on the clock, not at the next poll. */
export function servedAgeS(ageS: number | null, receivedAtMs: number, nowMs: number): number | null {
  return ageS === null ? null : ageS + Math.max(0, nowMs - receivedAtMs) / 1000;
}

/** One log line per refused scope: a refusal served in a 200 is drawn AND logged (AGENTS law 1). */
export function darkpoolRefusalLines(model: DarkpoolRailModel): string[] {
  if (!model.ok) return [`[DARK POOL] ${model.refused}: ${model.detail}`];
  return model.columns.flatMap((column) => column.ok ? [] : [`[DARK POOL] ${column.endpoint} ${column.refused}: ${column.detail}`]);
}

export function darkpoolRailModel(body: unknown): DarkpoolRailModel {
  if (!record(body) || body.root !== "SPY") {
    return { ok: false, refused: "ROOT_MISMATCH", detail: `served root is ${record(body) ? String(body.root) : "absent"}, expected SPY` };
  }
  if (typeof body.age_s !== "number" || !Number.isFinite(body.age_s) || body.age_s < 0
    || typeof body.budget_s !== "number" || !Number.isFinite(body.budget_s) || body.budget_s <= 0
    || typeof body.stale !== "boolean") {
    return { ok: false, refused: "PAYLOAD_INVALID", detail: "top-level freshness metadata is absent or invalid" };
  }
  if (!Array.isArray(body.clusters) || !record(body.rails)) {
    return { ok: false, refused: "PAYLOAD_INVALID", detail: "clusters or rails are absent" };
  }
  const clusters = body.clusters as DarkpoolLevelCluster[];
  if (clusters.some((item) => !record(item) || !RULES.some((rule) => item.source_endpoint === rule.endpoint))) {
    return { ok: false, refused: "PAYLOAD_INVALID", detail: "a cluster carries an unknown source_endpoint" };
  }
  const response = body as unknown as DarkpoolLevelsResponse;
  const columns = RULES.map((rule) => column(rule, response.rails[rule.endpoint], clusters)) as
    [DarkpoolRailColumn | DarkpoolRailColumnRefusal, DarkpoolRailColumn | DarkpoolRailColumnRefusal];
  return {
    ok: true,
    root: "SPY",
    columns,
  };
}
